/**
 * `/poste/aujourdhui` — the résidence's entry log since midnight.
 *
 * Two jobs. The obvious one is answering "has Mme Alami arrived yet?" without
 * the gardien leaving the desk, which is why it is searchable rather than just
 * scrollable — a busy morning is two hundred rows.
 *
 * The second is the escape hatch: a pass that exists but whose QR *and*
 * printed code are both unusable. `scan.record` with `source: "manual"`
 * force-writes an allow, deliberately, because the pass was already
 * authorised — nobody needs re-asking. That is emphatically NOT the walk-in
 * flow: a visitor with no pass at all goes through Arrivées, which asks the
 * résident. Keeping the two apart is the whole reason an always-allow button
 * is acceptable here.
 */
import type { RouterOutputs } from "@gately/api";
import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { LogIn, PencilLine, Search, X } from "lucide-react";

import { Button } from "@gately/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { toast } from "@gately/ui/toast";
import { unitLabel } from "@gately/validators";

import { OnSiteSection } from "~/components/poste/on-site-section";
import { StationChrome } from "~/components/station/station-chrome";
import { StationEyebrow } from "~/components/station/station-screen";
import { orpc } from "~/lib/orpc";
import {
  clockTime,
  denyReasonLabel,
  sourceLabel,
} from "~/lib/station/scan-format";
import { useOrgTimeZone } from "~/lib/use-me";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/poste/aujourdhui")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(orpc.shift.current.queryOptions()),
      context.queryClient.ensureQueryData(orpc.scan.today.queryOptions()),
      // `useTerms` reads it suspensefully for the vertical's vocabulary.
      context.queryClient.ensureQueryData(orpc.me.get.queryOptions()),
    ]);
  },
  component: TodayPage,
});

type Entry = RouterOutputs["scan"]["today"][number];

function TodayPage() {
  const { data: rows } = useSuspenseQuery({
    ...orpc.scan.today.queryOptions(),
    // Shared desk: another gardien on another device is writing to the same
    // log, and the syndic's approvals land here too.
    refetchInterval: 30_000,
  });
  const [query, setQuery] = useState("");
  const [manualOpen, setManualOpen] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => r.guestName.toLowerCase().includes(needle));
  }, [rows, query]);

  const admitted = rows.filter((r) => r.result === "allow").length;
  const refused = rows.length - admitted;

  return (
    <StationChrome>
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              Aujourd&apos;hui
            </h1>
            <StationEyebrow className="mt-1.5">
              Passages enregistrés · {admitted} entrée
              {admitted === 1 ? "" : "s"} · {refused} refus
            </StationEyebrow>
          </div>
          <Button
            variant="outline"
            className="h-12"
            onClick={() => setManualOpen(true)}
          >
            <PencilLine className="size-4" />
            Aucun QR lisible
          </Button>
        </div>

        <div className="relative mt-6">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2"
            aria-hidden="true"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Chercher un nom…"
            aria-label="Chercher un nom dans le journal"
            className="h-12 pl-10 text-base"
          />
        </div>

        <OnSiteSection hasLogRows={rows.length > 0} />

        <div className="mt-4 divide-y divide-white/10">
          {filtered.length === 0 ? (
            <p className="text-muted-foreground py-12 text-center text-base">
              {rows.length === 0
                ? "Aucun passage aujourd'hui."
                : "Aucun passage à ce nom."}
            </p>
          ) : (
            filtered.map((row) => <EntryRow key={row.id} row={row} />)
          )}
        </div>
      </div>

      <ManualEntryDialog open={manualOpen} onOpenChange={setManualOpen} />
    </StationChrome>
  );
}

function EntryRow({ row }: { row: Entry }) {
  const timeZone = useOrgTimeZone();
  const terms = useTerms();
  const denied = row.result === "deny";
  return (
    <div className="flex items-center gap-4 py-3.5">
      <span
        className={
          denied
            ? "bg-destructive/15 text-destructive flex size-9 shrink-0 items-center justify-center rounded-full"
            : "bg-success/15 text-success flex size-9 shrink-0 items-center justify-center rounded-full"
        }
      >
        {denied ? (
          <X className="size-4" strokeWidth={2.4} aria-hidden="true" />
        ) : (
          <LogIn className="size-4" strokeWidth={2.4} aria-hidden="true" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-semibold">{row.guestName}</p>
        <p className="text-muted-foreground truncate text-[0.8125rem]">
          {denied
            ? denyReasonLabel(row.reason)
            : `${row.gateName ?? "Portail"} · ${sourceLabel(row.source, terms)}`}
        </p>
      </div>
      <span className="text-muted-foreground shrink-0 font-mono text-sm">
        {clockTime(row.createdAt, timeZone)}
      </span>
    </div>
  );
}

function ManualEntryDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const terms = useTerms();
  const [unitId, setUnitId] = useState<string | undefined>(undefined);
  const [guestName, setGuestName] = useState("");
  const [note, setNote] = useState("");
  const [headcount, setHeadcount] = useState("");

  // Fetched only when the dialog is open — the desk's steady state is the
  // scanner, and a résidence's unit list is not small.
  const units = useQuery({ ...orpc.unit.list.queryOptions(), enabled: open });

  const record = useMutation(
    orpc.scan.record.mutationOptions({
      onSuccess: async () => {
        toast.success("Entrée enregistrée au journal.");
        setUnitId(undefined);
        setGuestName("");
        setNote("");
        setHeadcount("");
        onOpenChange(false);
        await queryClient.invalidateQueries({
          queryKey: orpc.scan.today.key(),
        });
      },
      onError: (e) =>
        toast.error(e.message || "L'entrée n'a pas pu être enregistrée."),
    }),
  );

  const canSubmit = Boolean(unitId) && guestName.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Entrée sans QR lisible</DialogTitle>
          <DialogDescription>
            Pour un invité qui possède bien une invitation, quand ni le QR ni le
            code imprimé ne peuvent être lus. Pour un visiteur sans invitation,
            utilisez « Arrivées ».
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!unitId || !canSubmit) return;
            record.mutate({
              source: "manual",
              unitId,
              guestName: guestName.trim(),
              note: note.trim() || undefined,
              actualHeadcount: headcount.trim()
                ? Number.parseInt(headcount, 10)
                : undefined,
            });
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="manual-unit">Destination</Label>
            <Select value={unitId} onValueChange={setUnitId}>
              <SelectTrigger id="manual-unit" className="w-full">
                <SelectValue
                  placeholder={
                    units.isPending
                      ? "Chargement…"
                      : `Choisir une ${terms.unitSingular}`
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(units.data ?? []).map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {unitLabel(u)}
                    {u.ownerName ? ` · ${u.ownerName}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="manual-name">Nom de l&apos;invité</Label>
            <Input
              id="manual-name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="ex. Glovo — livraison"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="manual-note">
              Note{" "}
              <span className="text-muted-foreground font-normal">
                (optionnel)
              </span>
            </Label>
            <Input
              id="manual-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="QR illisible — pièce d'identité vérifiée"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="manual-headcount">
              Personnes présentes{" "}
              <span className="text-muted-foreground font-normal">
                (optionnel)
              </span>
            </Label>
            <Input
              id="manual-headcount"
              inputMode="numeric"
              value={headcount}
              onChange={(e) =>
                setHeadcount(e.target.value.replace(/\D/g, "").slice(0, 2))
              }
              placeholder="ex. 3"
              className="w-24"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={!canSubmit || record.isPending}>
              {record.isPending ? "Enregistrement…" : "Enregistrer l'entrée"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
